import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  Button,
  Flex,
  IconButton,
  Text,
  DataList,
  Checkbox,
  Callout,
} from "@radix-ui/themes";
import {
  Cross1Icon,
  Pencil1Icon,
  PlusIcon,
  TrashIcon,
  DragHandleDots2Icon,
} from "@radix-ui/react-icons";
import {
  Sources,
  TrackerCreateOrUpdateInput,
} from "@/v2/entities/tracker/Tracker.api";
import {
  TrackerFieldType,
  TrackerStage,
  Tracker,
  TrackerAlertConfig,
  TrackerAlertAction,
  TrackerAlertWindowMinutes,
  trackerAlertWindowMinutesOptions,
  isAlertWindowMinutes,
} from "@/v2/entities/tracker/Tracker.model";
import { TrackerStageIcon } from "@/v2/assets/mor-shapes/TrackerStageIcon";
import { DndContext, closestCenter, DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import Select from "@/components/shared/select";
import { SimulatorChannelIcon } from "@/routes/simulators/SimulatorChannelIcon";
import { mockTrackerItem } from "@/routes/trackers/mockTrackerData";
import styles from "./trackerForm.module.scss";
import {
  ExploreFilterMenu,
  ExploreFiltersValue,
} from "../explore-filter-menu/ExploreFilterMenu";
import {
  buildFiltersFromFilter,
  parseFiltersToFilterRequest,
} from "../explore-filter-menu/explore-filter.utils";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import { trackerFieldTypeToExploreColumnType } from "@/v2/entities/tracker/Tracker.model";
import { exploreColumnTypeToLabel } from "@/v2/entities/explore/columns/ExploreColum.model";
import { Overview } from "../interaction-view/overview/Overview";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";

interface TrackerFormProps {
  mode: "create" | "update";
  initialData?: Tracker;
  onSubmit: (data: TrackerCreateOrUpdateInput) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

interface TrackerColumnWithLock {
  stage: TrackerStage;
  name: string;
  id: string;
  isLocked: boolean;
  order?: number;
  tags?: string[];
}

function getNextTrackerName() {
  const existingNames = ["Tracker 1", "Tracker 2", "Tracker 3"];
  return existingNames[Math.floor(Math.random() * existingNames.length)];
}

function SortableColumn({
  column,
  onRemove,
  isEditing,
  onNameChange,
  onStopEditing,
  onStartEditing,
}: {
  column: TrackerColumnWithLock;
  onRemove: (id: string) => void;
  isEditing: boolean;
  onNameChange: (id: string, value: string) => void;
  onStopEditing: () => void;
  onStartEditing: (id: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} className={styles.trackerFormColumn} style={style}>
      <div className={styles.trackerFormColumnContent}>
        {/* Drag handle */}
        <div
          {...attributes}
          {...listeners}
          className="dragHandle"
          style={{
            cursor: "grab",
            padding: "4px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <DragHandleDots2Icon />
        </div>
        <div style={{ flex: 1 }}>
          {isEditing ? (
            <input
              value={column.name}
              onChange={(e) => onNameChange(column.id, e.target.value)}
              onBlur={onStopEditing}
              onKeyDown={(e) => {
                if (e.key === "Enter") onStopEditing();
              }}
              className={styles.trackerFormColumnInput}
              autoFocus
            />
          ) : (
            <div className={styles.trackerFormColumnTitleRow}>
              <span
                className={
                  column.isLocked
                    ? `${styles.trackerFormColumnTitle} ${styles.trackerFormColumnTitleLocked}`
                    : styles.trackerFormColumnTitle
                }
              >
                {column.name}
                {!column.isLocked && (
                  <IconButton
                    variant="ghost"
                    color="gray"
                    size="1"
                    className={styles.trackerFormEditButton}
                    onClick={() =>
                      !column.isLocked && onStartEditing(column.id)
                    }
                    aria-label="Edit column name"
                  >
                    <Pencil1Icon />
                  </IconButton>
                )}
              </span>
              {column.name === "Captured" && (
                <span className={styles.trackerFormColumnDescription}>
                  All captured interactions will be added here automatically
                </span>
              )}
            </div>
          )}
        </div>
        {/* Show delete button for all columns except 'Captured' and only in create mode */}
        {column.name !== "Captured" && (
          <IconButton
            size="2"
            variant="soft"
            color="gray"
            onClick={() => onRemove(column.id)}
            aria-label="Delete column"
            className={styles.trackerFormDeleteButton}
          >
            <TrashIcon />
          </IconButton>
        )}
      </div>
    </div>
  );
}

export default function TrackerForm({
  mode,
  initialData,
  onSubmit,
  onCancel,
  loading = false,
}: TrackerFormProps) {
  // Initialize form state
  const [name, setName] = useState(() => {
    if (mode === "update" && initialData) {
      return initialData.name;
    }
    return getNextTrackerName();
  });
  const [editingName, setEditingName] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [editingColumnId, setEditingColumnId] = useState<string | null>(null);
  const columnNameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingName && nameInputRef.current) {
      nameInputRef.current.focus();
    }
    if (editingColumnId && columnNameInputRef.current) {
      columnNameInputRef.current.focus();
    }
  }, [editingName, editingColumnId]);

  const getInitialSources = (): Sources[] => {
    if (mode === "update" && initialData?.sources) {
      return initialData.sources;
    }
    return ["tickets"];
  };
  const [sources, setSources] = useState<Sources[]>(getInitialSources());
  const [filters, setFilters] = useState<ExploreFiltersValue>(() => {
    if (mode === "update" && initialData?.matchRules) {
      return buildFiltersFromFilter(initialData.matchRules as Filter);
    }
    return {};
  });
  const [columns, setColumns] = useState<TrackerColumnWithLock[]>(() => {
    if (mode === "update" && initialData) {
      // Sort by order if available, otherwise maintain array order
      const sortedColumns = [...initialData.columns].sort((a, b) => {
        if (typeof a.order === "number" && typeof b.order === "number") {
          return a.order - b.order;
        }
        return 0;
      });

      return sortedColumns.map((col, index) => ({
        ...col,
        id: col.id ?? uuidv4(),
        isLocked:
          col.stage === "to_do" && col.name === "Captured" ? true : false,
      }));
    }
    return [
      {
        stage: "to_do",
        name: "Captured",
        id: uuidv4(),
        isLocked: true,
        order: 0,
      },
      {
        stage: "in_progress",
        name: "In Progress",
        id: uuidv4(),
        isLocked: false,
        order: 0,
      },
      {
        stage: "done",
        name: "Done",
        id: uuidv4(),
        isLocked: false,
        order: 0,
      },
    ];
  });
  // --- LOCKED FIELDS ---
  const LOCKED_FIELDS: TrackerFieldType[] = [
    TrackerFieldType.subject,
    TrackerFieldType.displayId,
  ];

  const [selectedFields, setSelectedFields] = useState<TrackerFieldType[]>(
    () => {
      let fields: TrackerFieldType[] = [];
      if (mode === "update" && initialData) {
        fields = initialData.fields.map((f) => f.field);
      }
      // Always include locked fields at the start, in order
      fields = [
        ...LOCKED_FIELDS.filter((f) => !fields.includes(f)),
        ...fields.filter((f) => !LOCKED_FIELDS.includes(f)),
      ];
      return fields;
    }
  );

  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // --- Alert Config State ---
  const [isAlertEnabled, setIsAlertEnabled] = useState<boolean>(() =>
    mode === "update" ? Boolean(initialData?.alertConfig) : false
  );
  const [alertThreshold, setAlertThreshold] = useState<number>(() => {
    if (mode === "update" && initialData?.alertConfig?.thresholdCount)
      return initialData.alertConfig.thresholdCount;
    return 1;
  });
  const [alertWindow, setAlertWindow] = useState<TrackerAlertWindowMinutes>(
    () => {
      if (
        mode === "update" &&
        initialData?.alertConfig?.timeWindowMinutes &&
        isAlertWindowMinutes(initialData.alertConfig.timeWindowMinutes)
      )
        return initialData.alertConfig.timeWindowMinutes;
      return 10;
    }
  );
  const [slackEnabled, setSlackEnabled] = useState<boolean>(() => {
    if (mode === "update" && initialData?.alertConfig?.actions) {
      return initialData.alertConfig.actions.some((a) => a.type === "slack");
    }
    return true;
  });
  const [webhookUrls, setWebhookUrls] = useState<string[]>(() => {
    if (mode === "update" && initialData?.alertConfig?.actions) {
      const actions = initialData.alertConfig.actions;
      const webhookActions = actions.filter(
        (a): a is Extract<TrackerAlertAction, { type: "webhook" }> =>
          a.type === "webhook"
      );
      return webhookActions.map((a) => a.webhookUrl ?? "");
    }
    return [];
  });
  const [alertFilters, setAlertFilters] = useState<ExploreFiltersValue>(() => {
    if (mode === "update" && initialData?.alertConfig?.additionalConditions) {
      return buildFiltersFromFilter(
        initialData.alertConfig.additionalConditions
      );
    }
    return {};
  });

  const stageLabels = {
    to_do: "To Do",
    in_progress: "In Progress",
    done: "Done",
    hidden: "Ignore",
  };

  const availableStages = ["to_do", "in_progress", "done", "hidden"] as const;

  const sourceOptions = [
    {
      value: "tickets" as Sources,
      label: "Tickets",
      channel: "autonomous_ticket_event" as const,
    },
    {
      value: "conversations" as Sources,
      label: "Conversations",
      channel: "autonomous_quackchat" as const,
    },
  ];

  const fieldTypeOptions = useMemo(() => {
    return Object.values(TrackerFieldType)
      .filter((fieldType) => fieldType !== TrackerFieldType.id)
      .map((fieldType) => {
        const columnType = trackerFieldTypeToExploreColumnType[fieldType];
        const label = exploreColumnTypeToLabel[columnType];

        return {
          value: fieldType,
          label: label,
        };
      });
  }, []);

  const columnsByStage = useMemo(() => {
    const grouped: Record<string, typeof columns> = {};
    availableStages.forEach((stage) => {
      const stageColumns = columns.filter((col) => col.stage === stage);
      // Sort by global order to maintain the exact order from the form
      grouped[stage] = stageColumns.sort((a, b) => {
        if (typeof a.order === "number" && typeof b.order === "number") {
          return a.order - b.order;
        }
        return 0;
      });
    });
    return grouped;
  }, [columns]);

  const handleAddColumnToStage = useCallback(
    (stage: TrackerStage) => {
      const stageName = stageLabels[stage as keyof typeof stageLabels];

      // Find the highest order for this stage and add 1
      const stageColumns = columns.filter((col) => col.stage === stage);
      const maxOrder =
        stageColumns.length > 0
          ? Math.max(...stageColumns.map((col) => col.order || 0))
          : -1;

      const newOrder = maxOrder + 1;
      const newId = uuidv4();

      setColumns([
        ...columns,
        {
          stage,
          name: stageName,
          id: newId,
          isLocked: false,
          order: newOrder,
        },
      ]);
    },
    [columns, stageLabels]
  );

  const handleRemoveColumn = useCallback(
    (columnId: string) => {
      const column = columns.find((col) => col.id === columnId);
      if (column?.isLocked) {
        return; // Don't allow deletion of locked columns
      }
      setColumns(columns.filter((col) => col.id !== columnId));
    },
    [columns]
  );

  const handleColumnChange = useCallback(
    (columnId: string, key: "stage" | "name", value: string | TrackerStage) => {
      setColumns(
        columns.map((col) =>
          col.id === columnId ? { ...col, [key]: value } : col
        )
      );
    },
    [columns]
  );

  const handleColumnNameChange = useCallback(
    (columnId: string, value: string) => {
      handleColumnChange(columnId, "name", value);
    },
    [handleColumnChange]
  );

  const handleStartEditing = useCallback((columnId: string) => {
    setEditingColumnId(columnId);
  }, []);

  const handleStopEditing = useCallback(() => {
    setEditingColumnId(null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const activeColumn = columns.find((col) => col.id === active.id);
      const overColumn = columns.find((col) => col.id === over.id);
      if (!activeColumn || !overColumn) return;

      // Only allow reordering within the same stage
      if (activeColumn.stage !== overColumn.stage) return;

      // Find columns in the same stage
      const stageColumns = columns.filter(
        (col) => col.stage === activeColumn.stage
      );
      const activeIndex = stageColumns.findIndex((col) => col.id === active.id);
      const overIndex = stageColumns.findIndex((col) => col.id === over.id);

      // Create a new array with the reordered columns within the stage
      const newStageColumns = [...stageColumns];
      newStageColumns.splice(activeIndex, 1);
      newStageColumns.splice(overIndex, 0, activeColumn);

      // Update the order values within the stage
      const updatedStageColumns = newStageColumns.map((col, index) => ({
        ...col,
        order: index,
      }));

      // Update the main columns array
      const newColumns = columns.map((col) => {
        if (col.stage === activeColumn.stage) {
          const updatedCol = updatedStageColumns.find(
            (updated) => updated.id === col.id
          );
          return updatedCol || col;
        }
        return col;
      });

      setColumns(newColumns);
    },
    [columns]
  );

  // Prevent reordering locked fields
  const handleFieldDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = selectedFields.indexOf(active.id as TrackerFieldType);
      const newIndex = selectedFields.indexOf(over.id as TrackerFieldType);
      // Don't allow dragging locked fields or moving any field before locked fields
      if (
        LOCKED_FIELDS.includes(active.id as TrackerFieldType) ||
        newIndex < LOCKED_FIELDS.length
      )
        return;
      const newFields = [...selectedFields];
      const [removed] = newFields.splice(oldIndex, 1);
      newFields.splice(newIndex, 0, removed);
      setSelectedFields(newFields);
    },
    [selectedFields]
  );

  // Prevent adding locked fields again
  const handleAddField = useCallback(
    (field: TrackerFieldType) => {
      if (!selectedFields.includes(field) && !LOCKED_FIELDS.includes(field)) {
        setSelectedFields([...selectedFields, field]);
      }
    },
    [selectedFields]
  );

  // Prevent removing locked fields
  const handleRemoveField = useCallback(
    (field: TrackerFieldType) => {
      if (LOCKED_FIELDS.includes(field)) return;
      setSelectedFields(selectedFields.filter((f) => f !== field));
    },
    [selectedFields]
  );

  // Validation errors
  const validationErrors = useMemo(() => {
    const errors: string[] = [];

    if (!name.trim()) {
      errors.push("Tracker name is required");
    }

    if (sources.length === 0) {
      errors.push(
        "Please select at least one data source (tickets or conversations)"
      );
    }

    if (columns.length === 0) {
      errors.push("Please add at least one column to define workflow stages");
    }

    if (selectedFields.length === 0) {
      errors.push("Please select at least one field to display in the tracker");
    }

    if (isAlertEnabled) {
      if (!Number.isFinite(alertThreshold) || alertThreshold < 1) {
        errors.push("Alert threshold must be at least 1");
      }
      if (!isAlertWindowMinutes(alertWindow)) {
        errors.push("Invalid alert time window");
      }
      const invalidWebhook = webhookUrls.some((url) => {
        if (!url) return false;
        try {
          new URL(url);
          return false;
        } catch {
          return true;
        }
      });
      if (invalidWebhook) errors.push("One or more webhook URLs are invalid");
    }

    return errors;
  }, [
    name,
    sources,
    columns,
    selectedFields,
    isAlertEnabled,
    alertThreshold,
    alertWindow,
    webhookUrls,
  ]);

  const handleSubmit = useCallback(async () => {
    setHasAttemptedSubmit(true);

    if (hasAttemptedSubmit && validationErrors.length > 0) {
      toast.error(
        validationErrors.length === 1
          ? validationErrors[0]
          : `Please complete the following: ${validationErrors.join(", ")}`
      );
      return;
    }

    const matchRules =
      Object.keys(filters).length > 0
        ? parseFiltersToFilterRequest(filters)
        : {};

    const trackerData: TrackerCreateOrUpdateInput = {
      name,
      sources: sources,
      columns: columns
        .filter((column) => column.stage !== "hidden")
        .map((column) => ({
          name: column.name,
          stage: column.stage as "to_do" | "in_progress" | "done",
          tags: column.tags || [],
          order: column.order,
          id: column.id,
        })),
      fields: selectedFields,
      matchRules,
    };

    if (isAlertEnabled) {
      const actions: TrackerAlertAction[] = [];
      if (slackEnabled) actions.push({ type: "slack" });
      webhookUrls
        .filter((url) => url && url.trim().length > 0)
        .forEach((url) =>
          actions.push({ type: "webhook", webhookUrl: url.trim() })
        );

      const additionalConditions =
        Object.keys(alertFilters).length > 0
          ? parseFiltersToFilterRequest(alertFilters)
          : null;

      const alertConfig: TrackerAlertConfig = {
        thresholdCount: Math.max(1, Math.floor(alertThreshold)),
        timeWindowMinutes: alertWindow,
        additionalConditions,
        actions,
      };

      trackerData.alertConfig = alertConfig;
    } else {
      trackerData.alertConfig = null;
    }

    await onSubmit(trackerData);
  }, [
    name,
    sources,
    columns,
    selectedFields,
    filters,
    isAlertEnabled,
    alertThreshold,
    alertWindow,
    slackEnabled,
    webhookUrls,
    alertFilters,
    onSubmit,
    validationErrors,
  ]);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setName(e.target.value);
    },
    []
  );

  const handleNameBlur = useCallback(() => {
    setEditingName(false);
  }, []);

  const handleNameKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") setEditingName(false);
    },
    []
  );

  const handleEditName = useCallback(() => {
    setEditingName(true);
  }, []);

  const handleSourceChange = useCallback((value: Sources) => {
    setSources((prev) => {
      if (prev.includes(value)) {
        return prev.filter((s) => s !== value);
      } else {
        return [...prev, value];
      }
    });
  }, []);

  const handleActiveFiltersChange = useCallback(
    (filters: ExploreFiltersValue) => {
      setFilters(filters);
    },
    []
  );

  const SortableFieldItem = useCallback(
    ({
      field,
      onRemove,
    }: {
      field: TrackerFieldType;
      onRemove: () => void;
    }) => {
      const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
      } = useSortable({ id: field });

      const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      };

      return (
        <DataList.Item
          ref={setNodeRef}
          style={style}
          className={isDragging ? "draggingField" : ""}
        >
          <div className={styles.trackerFormFieldRow}>
            <div
              {...attributes}
              {...listeners}
              className="dragHandle"
              style={{ cursor: "grab", padding: "4px" }}
            >
              <DragHandleDots2Icon />
            </div>

            <Flex flexGrow={"1"}>
              <Overview
                lite
                source={sources[0]}
                interaction={mockTrackerItem.interactionData}
                visibleColumns={[trackerFieldTypeToExploreColumnType[field]]}
              />

              <IconButton
                size="2"
                variant="soft"
                color="gray"
                onClick={onRemove}
                aria-label="Delete field"
                className={styles.trackerFormFieldDeleteButton}
              >
                <TrashIcon />
              </IconButton>
            </Flex>
          </div>
        </DataList.Item>
      );
    },
    [mockTrackerItem]
  );

  // Helper for dynamic step 2 header text
  const sourceLabels = {
    tickets: "tickets",
    conversations: "conversations",
  };
  const selectedLabels = sources.map(
    (s) => sourceLabels[s as keyof typeof sourceLabels]
  );
  const dynamicText =
    selectedLabels.length === 2
      ? "conversations and tickets"
      : selectedLabels[0] || "";

  const filterFields = useMemo(() => {
    return selectedFields.filter((field) => !LOCKED_FIELDS.includes(field));
  }, [selectedFields]);

  return (
    <div className={styles.trackerFormContainer}>
      {/* Header */}
      <div className={styles.trackerFormHeader}>
        <div className={styles.trackerFormHeaderLeft}>
          <button
            onClick={onCancel}
            className={styles.trackerFormCloseButton}
            aria-label="Close"
          >
            <Cross1Icon
              style={{ width: "16px", height: "16px", color: "var(--gray-11)" }}
            />
          </button>
          <span className={styles.trackerFormHeaderTitle}>
            {mode === "create" ? "New Tracker" : "Edit Tracker"}
          </span>
        </div>
        <div className={styles.trackerFormHeaderActions}>
          <Button
            onClick={handleSubmit}
            color="gray"
            highContrast
            loading={loading}
            disabled={false}
          >
            {mode === "create" ? "Create" : "Update"}
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className={styles.trackerFormMainContent}>
        {/* Name section */}
        <div className={styles.trackerFormNameSection}>
          {editingName ? (
            <input
              ref={nameInputRef}
              value={name}
              onChange={handleNameChange}
              onBlur={handleNameBlur}
              onKeyDown={handleNameKeyDown}
              className={styles.trackerFormNameInput}
              placeholder="Enter tracker name..."
            />
          ) : (
            <>
              <span
                className={styles.trackerFormNameDisplay}
                onClick={handleEditName}
              >
                {name || "New Tracker"}
              </span>
              <IconButton
                variant="ghost"
                color="gray"
                size="1"
                className={styles.trackerFormEditButton}
                onClick={handleEditName}
                aria-label="Edit name"
              >
                <Pencil1Icon />
              </IconButton>
            </>
          )}
        </div>

        {/* Step 1: Choose Interactions */}
        <div className={styles.trackerFormStepSection}>
          <div className={styles.trackerFormStepHeader}>
            <div className={styles.trackerFormStepNumber}>1</div>
            <div className={styles.trackerFormStepInfo}>
              <div className={styles.trackerFormStepTitle}>
                Choose Interactions
              </div>
              <div className={styles.trackerFormStepDescription}>
                Select data source
              </div>
            </div>
          </div>
          <div className={styles.trackerFormStepContent}>
            <div className={styles.trackerFormStepContentInner}>
              <div className={styles.trackerFormStepText}>
                This tracker will be used for
              </div>
              <div className={styles.trackerFormSourceOptions}>
                {sourceOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`${styles.trackerFormSourceOption} ${
                      sources.includes(option.value)
                        ? styles.trackerFormSourceOptionSelected
                        : ""
                    }`}
                    onClick={() => handleSourceChange(option.value)}
                  >
                    <div className={styles.trackerFormSourceOptionContent}>
                      <Checkbox
                        checked={sources.includes(option.value)}
                        color="gray"
                        highContrast
                        className={styles.trackerFormSourceOptionCheckbox}
                      />
                      <SimulatorChannelIcon channel={option.channel} size="2" />
                      <span className={styles.trackerFormSourceOptionLabel}>
                        {option.label}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Step 2: Capture Rules */}
        <div className={styles.trackerFormStepSection}>
          <div className={styles.trackerFormStepHeader}>
            <div className={styles.trackerFormStepNumber}>2</div>
            <div className={styles.trackerFormStepInfo}>
              <div className={styles.trackerFormStepTitle}>Capture Rules</div>
              <div className={styles.trackerFormStepDescription}>
                Define match criteria
              </div>
            </div>
          </div>
          <div className={styles.trackerFormStepContent}>
            <div className={styles.trackerFormStepContentInner}>
              <div className={styles.trackerFormStepText}>
                This tracker will capture {dynamicText} matching:
              </div>
              <Flex mt="2">
                <ExploreFilterMenu
                  source={sources.length === 1 ? sources[0] : undefined}
                  label="Rules"
                  size="2"
                  value={filters}
                  onChange={handleActiveFiltersChange}
                  hideCount
                />
              </Flex>
              {Object.keys(filters).length === 0 && (
                <Callout.Root
                  variant="soft"
                  color="blue"
                  className={styles.trackerFormCallout}
                >
                  <Callout.Icon>
                    <span>👉</span>
                  </Callout.Icon>
                  <Callout.Text>
                    Add filters to capture specific interactions based on
                    criteria like team, channel, or custom enrichments.
                  </Callout.Text>
                </Callout.Root>
              )}
            </div>
          </div>
        </div>

        {/* Step 3: Set Columns */}
        <div className={styles.trackerFormStepSection}>
          <div className={styles.trackerFormStepHeader}>
            <div className={styles.trackerFormStepNumber}>3</div>
            <div className={styles.trackerFormStepInfo}>
              <div className={styles.trackerFormStepTitle}>Set Columns</div>
              <div className={styles.trackerFormStepDescription}>
                Define workflow stages
              </div>
            </div>
          </div>
          <div className={styles.trackerFormStepContent}>
            <div className={styles.trackerFormStepContentInner}>
              <div className={styles.trackerFormStepText}>
                Define the workflow stages for your tracker:
              </div>

              {/* Visual Column Preview - Vertical List */}
              <div className={styles.trackerFormColumnsContainer}>
                {Object.entries(columnsByStage).map(([stage, stageColumns]) => {
                  return (
                    <div key={stage} className={styles.trackerFormStageSection}>
                      <div className={styles.trackerFormStageHeader}>
                        <TrackerStageIcon
                          stage={stage as TrackerStage}
                          width={12}
                          height={12}
                        />
                        <span className={styles.trackerFormStageTitle}>
                          {stageLabels[stage as keyof typeof stageLabels]}
                        </span>
                        {(stage === "in_progress" ||
                          stage === "done" ||
                          stage === "hidden") && (
                          <IconButton
                            size="2"
                            variant="soft"
                            color="gray"
                            onClick={() => handleAddColumnToStage(stage)}
                            aria-label={`Add ${
                              stageLabels[stage as keyof typeof stageLabels]
                            } column`}
                            className={styles.trackerFormAddColumnButton}
                          >
                            <PlusIcon />
                          </IconButton>
                        )}
                      </div>
                      <div className={styles.trackerFormStageColumns}>
                        {stageColumns.length === 0 &&
                        (stage === "in_progress" ||
                          stage === "done" ||
                          stage === "hidden") &&
                        mode === "create" ? (
                          <div className={styles.trackerFormEmptyStage}>
                            <div className={styles.trackerFormEmptyStageTitle}>
                              No columns
                            </div>
                            <div
                              className={
                                styles.trackerFormEmptyStageDescription
                              }
                            >
                              Add columns to this stage
                            </div>
                          </div>
                        ) : (
                          <DndContext
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                          >
                            <SortableContext
                              items={stageColumns.map((col) => col.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              {stageColumns.map((column) => (
                                <SortableColumn
                                  key={column.id}
                                  column={column}
                                  onRemove={handleRemoveColumn}
                                  isEditing={editingColumnId === column.id}
                                  onNameChange={handleColumnNameChange}
                                  onStopEditing={handleStopEditing}
                                  onStartEditing={handleStartEditing}
                                />
                              ))}
                            </SortableContext>
                          </DndContext>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Step 4: Configure View */}
        <div className={styles.trackerFormStepSection}>
          <div className={styles.trackerFormStepHeader}>
            <div className={styles.trackerFormStepNumber}>4</div>
            <div className={styles.trackerFormStepInfo}>
              <div className={styles.trackerFormStepTitle}>Configure View</div>
              <div className={styles.trackerFormStepDescription}>
                Select display fields
              </div>
            </div>
          </div>
          <div className={styles.trackerFormStepContent}>
            <div className={styles.trackerFormStepContentInner}>
              <div className={styles.trackerFormStepText}>
                Choose which fields to display in your tracker view:
              </div>
              <div className={styles.trackerFormPreviewContainer}>
                <Text
                  size="2"
                  weight="medium"
                  className={styles.trackerFormPreviewTitle}
                >
                  Preview
                </Text>
                <div className={styles.trackerFormPreviewCard}>
                  <div className={styles.trackerFormPreviewHeader}>
                    <div className={styles.trackerFormPreviewHeaderContent}>
                      <Text size="2" weight="medium" highContrast>
                        {mockTrackerItem.interactionData.subject}
                      </Text>
                      <Text size="1" color="gray">
                        #{mockTrackerItem.interactionData.displayId}
                      </Text>
                    </div>
                  </div>
                  <DndContext
                    collisionDetection={closestCenter}
                    onDragEnd={handleFieldDragEnd}
                  >
                    <SortableContext
                      items={selectedFields.filter(
                        (f) => !LOCKED_FIELDS.includes(f)
                      )}
                      strategy={verticalListSortingStrategy}
                    >
                      <DataList.Root className={styles.trackerFormPreviewList}>
                        {filterFields.length === 0 ? (
                          <div className={styles.trackerFormPreviewEmpty}>
                            <Text size="2" color="gray">
                              No additional fields selected.
                            </Text>
                          </div>
                        ) : (
                          filterFields.map((field) => (
                            <SortableFieldItem
                              key={field}
                              field={field}
                              onRemove={() => handleRemoveField(field)}
                            />
                          ))
                        )}
                      </DataList.Root>
                    </SortableContext>
                  </DndContext>
                  <div className={styles.trackerFormAddField}>
                    <DropdownMenu
                      items={fieldTypeOptions
                        .filter(
                          (option) =>
                            !selectedFields.includes(
                              option.value as TrackerFieldType
                            ) &&
                            !LOCKED_FIELDS.includes(
                              option.value as TrackerFieldType
                            )
                        )
                        .map((option) => ({
                          label: option.label,
                          onClick: () =>
                            handleAddField(option.value as TrackerFieldType),
                        }))}
                    >
                      <DropdownMenu.Trigger>
                        <Button
                          color="gray"
                          variant="outline"
                          highContrast
                          size="2"
                          className={styles.trackerFormAddFieldButton}
                        >
                          <PlusIcon
                            className={styles.trackerFormAddFieldIcon}
                          />{" "}
                          Add Field
                        </Button>
                      </DropdownMenu.Trigger>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step 5: Alert Config */}

        <div className={styles.trackerFormStepSection}>
          <div className={styles.trackerFormStepHeader}>
            <div className={styles.trackerFormStepNumber}>5</div>
            <div className={styles.trackerFormStepInfo}>
              <div className={styles.trackerFormStepTitle}>Alerts</div>
              <div className={styles.trackerFormStepDescription}>
                Configure alert threshold, time window, and actions
              </div>
            </div>
          </div>
          <div className={styles.trackerFormStepContent}>
            <div className={styles.trackerFormStepContentInner}>
              <div className={styles.trackerFormStepText}>
                Enable custom alerting when captures exceed a threshold:
              </div>
              <div
                className={`${styles.alertConfigRow} ${styles.alertConfigRowSpacing}`}
              >
                <Checkbox
                  checked={isAlertEnabled}
                  onCheckedChange={(v) => setIsAlertEnabled(Boolean(v))}
                  color="gray"
                  highContrast
                />
                <Text size="2">Enable Alerts</Text>
              </div>

              {isAlertEnabled && (
                <div className={styles.alertConfigContainer}>
                  <div className={styles.alertConfigRow}>
                    <Text size="2" weight="medium">
                      Threshold
                    </Text>
                    <input
                      type="number"
                      min={1}
                      value={alertThreshold}
                      onChange={(e) =>
                        setAlertThreshold(Number(e.target.value))
                      }
                      className={`${styles.trackerFormNameInput} ${styles.alertThresholdInput}`}
                    />
                    <Text size="2" color="gray">
                      captures in
                    </Text>
                    <Select
                      value={String(alertWindow)}
                      onChange={(value) => {
                        const next = Number(value);
                        if (isAlertWindowMinutes(next)) setAlertWindow(next);
                      }}
                      options={trackerAlertWindowMinutesOptions.map(
                        (minutes) => ({
                          label: `${minutes} minutes`,
                          value: String(minutes),
                        })
                      )}
                      portal={false}
                    />
                  </div>

                  <div
                    className={`${styles.alertConfigRow} ${styles.alertConfigRowSmallSpacing}`}
                  >
                    <Checkbox
                      checked={slackEnabled}
                      onCheckedChange={(v) => setSlackEnabled(Boolean(v))}
                      color="gray"
                      highContrast
                    />
                    <Text size="2">Slack notification</Text>
                  </div>

                  <div>
                    <Text size="2" weight="medium">
                      Webhook actions
                    </Text>
                    <div className={styles.alertWebhookList}>
                      {webhookUrls.map((url, idx) => (
                        <div key={idx} className={styles.alertWebhookRow}>
                          <input
                            type="url"
                            placeholder="https://example.com/webhook"
                            value={url}
                            onChange={(e) => {
                              const next = [...webhookUrls];
                              next[idx] = e.target.value;
                              setWebhookUrls(next);
                            }}
                            className={`${styles.trackerFormNameInput} ${styles.alertUrlInput}`}
                          />
                          <IconButton
                            size="2"
                            variant="soft"
                            color="gray"
                            onClick={() =>
                              setWebhookUrls(
                                webhookUrls.filter((_, i) => i !== idx)
                              )
                            }
                            aria-label="Remove webhook"
                          >
                            <TrashIcon />
                          </IconButton>
                        </div>
                      ))}
                      <Button
                        color="gray"
                        variant="outline"
                        highContrast
                        size="2"
                        onClick={() => setWebhookUrls([...webhookUrls, ""])}
                        style={{ width: "fit-content" }}
                      >
                        <PlusIcon className={styles.trackerFormAddFieldIcon} />{" "}
                        Add webhook
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Text size="2" weight="medium">
                      Additional filters (optional)
                    </Text>
                    <Flex mt="2">
                      <ExploreFilterMenu
                        source={sources.length === 1 ? sources[0] : undefined}
                        label="Alert filters"
                        size="2"
                        value={alertFilters}
                        onChange={setAlertFilters}
                        hideCount
                      />
                    </Flex>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
